import { createFileRoute, redirect } from '@tanstack/react-router';
import { configRouteRedirect } from '~/lib/config-access';
import { useState, type FormEvent } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Code2,
  Plus,
  ShieldAlert,
  CheckCircle2,
  X,
  AlertCircle,
} from 'lucide-react';
import { pb, currentUser } from '~/lib/pb';
import type { Tenant, CmsSchemaRecord } from '~/lib/types';
import { validateContent, defaultsForSchema } from '@focus/schema';
import type { CmsSchema } from '@focus/schema';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { useTenantContext } from '~/lib/tenantContext';
import styles from './_authed.schemas.module.css';

// /schemas redirige a /settings?tab=schemas — el contenido se rendea
// embebido en Settings (refactor 2026-05-06: configuración consolidada).
export const Route = createFileRoute('/_authed/schemas')({
  beforeLoad: () => {
    const dest = configRouteRedirect(currentUser()?.role, '/schemas');
    if (dest) throw redirect(dest);
    throw redirect({ to: '/settings', search: { tab: 'schemas' } });
  },
});

interface SchemaWithTenant extends CmsSchemaRecord {
  expand?: { tenant?: Tenant };
}

function useSchemas() {
  return useQuery({
    queryKey: ['cms_schemas', 'all'],
    queryFn: async () => {
      const list = await pb.collection('cms_schemas').getFullList<SchemaWithTenant>({
        sort: '-is_active,-version',
        expand: 'tenant',
      });
      return list;
    },
  });
}

function useTenantsList() {
  return useQuery({
    queryKey: ['tenants', 'all'],
    queryFn: () =>
      pb.collection('tenants').getFullList<Tenant>({ sort: 'name' }),
  });
}

function useSaveSchema() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      tenantId: string;
      schema: CmsSchema;
      description?: string;
      activate: boolean;
    }) => {
      const payload: Record<string, unknown> = {
        tenant: input.tenantId,
        version: input.schema.version,
        schema: input.schema,
        description: input.description ?? '',
        is_active: input.activate,
      };
      // Si activamos este schema, desactivar los demás del mismo tenant.
      if (input.activate) {
        const others = await pb
          .collection('cms_schemas')
          .getFullList<CmsSchemaRecord>({
            filter: `tenant = "${input.tenantId}" && is_active = true${input.id ? ` && id != "${input.id}"` : ''}`,
          });
        for (const o of others) {
          await pb.collection('cms_schemas').update(o.id, { is_active: false });
        }
      }
      if (input.id) {
        return pb.collection('cms_schemas').update<CmsSchemaRecord>(input.id, payload);
      }
      return pb.collection('cms_schemas').create<CmsSchemaRecord>(payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cms_schemas'] }),
  });
}

/**
 * Vista de schemas embebida en Settings. Sin wrapper de página ni h1
 * principal — el wrapper lo provee el tab panel de Settings.
 */
export function SchemasView() {
  const me = currentUser();
  const isSuperadmin = me?.role === 'superadmin';
  const schemasQ = useSchemas();
  const tenantsQ = useTenantsList();
  const { selectedTenantId, selectedTenant } = useTenantContext();
  const [editTarget, setEditTarget] = useState<SchemaWithTenant | null>(null);
  const [creating, setCreating] = useState(false);

  // Filtra al cliente activo (Settings es siempre per-tenant): muestra solo los
  // schemas del tenant que se está viendo, no todos.
  const rows = (schemasQ.data ?? []).filter((s) => s.tenant === selectedTenantId);

  if (!isSuperadmin) {
    return (
      <div className={styles.notSuperadmin}>
        <ShieldAlert size={32} aria-hidden style={{ marginBottom: 12 }} />
        <p>Solo superadmins gestionan schemas del CMS.</p>
      </div>
    );
  }

  return (
    <div className={styles.embedded}>
      <div className={styles.embeddedHead}>
        <p className={styles.subtitle}>
          Versiones del sitio de <strong>{selectedTenant?.name ?? 'este cliente'}</strong>. Cada
          cliente tiene 0 o 1 versión activa. El editor valida la estructura antes de guardar —
          si el JSON es inválido, no se persiste.
        </p>
        <button
          type="button"
          className={styles.button}
          onClick={() => setCreating(true)}
        >
          <Plus size={16} aria-hidden />
          Nuevo schema
        </button>
      </div>

      {schemasQ.isLoading && <div className={styles.empty}>Cargando…</div>}

      {!schemasQ.isLoading && rows.length === 0 && (
        <div className={styles.empty}>
          {selectedTenant?.name ?? 'Este cliente'} todavía no tiene schemas. Crea el primero con el botón arriba.
        </div>
      )}

      {rows.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Versión</th>
              <th>Estado</th>
              <th>Secciones</th>
              <th>Actualizado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const sectionsCount = Object.keys(
                ((s.schema as CmsSchema | undefined)?.sections ?? {}),
              ).length;
              return (
                <tr key={s.id}>
                  <td>v{s.version}</td>
                  <td>
                    {s.is_active ? (
                      <span className={styles.statusActive}>
                        <CheckCircle2 size={12} aria-hidden /> Activo
                      </span>
                    ) : (
                      <span className={styles.muted}>Inactivo</span>
                    )}
                  </td>
                  <td>{sectionsCount}</td>
                  <td className={styles.muted}>
                    {new Date(s.updated).toLocaleDateString('es-CL')}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={() => setEditTarget(s)}
                      title="Editar JSON"
                    >
                      <Code2 size={14} aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editTarget && (
        <SchemaEditor
          target={editTarget}
          tenants={tenantsQ.data ?? []}
          onClose={() => setEditTarget(null)}
        />
      )}
      {creating && (
        <SchemaEditor
          target={null}
          tenants={tenantsQ.data ?? []}
          defaultTenantId={selectedTenantId ?? undefined}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}

// ── Editor JSON ────────────────────────────────────────────────────────

function SchemaEditor({
  target,
  tenants,
  defaultTenantId,
  onClose,
}: {
  target: SchemaWithTenant | null;
  tenants: Tenant[];
  defaultTenantId?: string;
  onClose: () => void;
}) {
  const isEdit = !!target;
  const save = useSaveSchema();
  const confirm = useConfirm();

  const [tenantId, setTenantId] = useState<string>(target?.tenant ?? defaultTenantId ?? tenants[0]?.id ?? '');
  const [description, setDescription] = useState<string>(target?.description ?? '');
  const [jsonText, setJsonText] = useState<string>(() =>
    target ? JSON.stringify(target.schema, null, 2) : exampleSchema(),
  );
  const [activate, setActivate] = useState<boolean>(target?.is_active ?? true);
  const [error, setError] = useState<string | null>(null);
  const [validationOk, setValidationOk] = useState<string | null>(null);

  function handleValidate(): CmsSchema | null {
    setError(null);
    setValidationOk(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      setError(`JSON inválido: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed) ||
      typeof (parsed as CmsSchema).version !== 'number' ||
      !(parsed as CmsSchema).sections ||
      typeof (parsed as CmsSchema).sections !== 'object'
    ) {
      setError(
        'El schema debe ser un objeto con `version: number` y `sections: { … }`.',
      );
      return null;
    }
    const schema = parsed as CmsSchema;
    // Verificación adicional: que el schema produzca defaults sin crashear,
    // y que validar esos defaults contra el schema NO falle por shape inválida.
    try {
      const defaults = defaultsForSchema(schema);
      const result = validateContent(schema, defaults);
      // Defaults con muchos `required` van a fallar — eso NO es un error del
      // schema, así que ignoramos errores de "es obligatorio". Solo reportamos
      // errores estructurales (debe ser objeto, debe ser lista, etc.).
      const structural = result.errors.filter((e) => !/obligatorio/.test(e.message));
      if (structural.length > 0) {
        setError(
          `El schema tiene ${structural.length} error(es) estructurales:\n` +
            structural
              .slice(0, 5)
              .map((e) => `• ${e.path}: ${e.message}`)
              .join('\n'),
        );
        return null;
      }
    } catch (e) {
      setError(
        `El schema no se puede instanciar: ${e instanceof Error ? e.message : String(e)}`,
      );
      return null;
    }
    setValidationOk(
      `✓ JSON válido. ${Object.keys(schema.sections).length} secciones, version ${schema.version}.`,
    );
    return schema;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!tenantId) {
      setError('Tenant requerido');
      return;
    }
    const schema = handleValidate();
    if (!schema) return;
    if (activate) {
      const ok = await confirm({
        title: 'Activar este schema',
        description: (
          <>
            Esto desactiva cualquier otro schema activo del cliente. Si el contenido publicado actual no matchea el shape del nuevo schema, los formularios pueden mostrar campos vacíos o errores hasta que vuelvas a guardarlos.
          </>
        ),
        confirmLabel: 'Activar y guardar',
        tone: 'primary',
      });
      if (!ok) return;
    }
    try {
      await save.mutateAsync({
        id: target?.id,
        tenantId,
        schema,
        description,
        activate,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    }
  }

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <form
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className={styles.modal__header}>
          <h2>{isEdit ? `Editar schema v${target?.version}` : 'Nuevo schema'}</h2>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={16} aria-hidden />
          </button>
        </div>
        <div className={styles.modal__body}>
          <div className={styles.row2}>
            <label className={styles.field}>
              <span className={styles.field__label}>Cliente</span>
              <select
                className={styles.select}
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                disabled={isEdit}
                required
              >
                <option value="">— elegir —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.field__label}>Descripción (opcional)</span>
              <input
                type="text"
                className={styles.input}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Notas para el cambio (ej: 'agrega sección landing')"
              />
            </label>
          </div>

          <label className={styles.field}>
            <span className={styles.field__label}>JSON del schema</span>
            <textarea
              className={styles.json}
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                setValidationOk(null);
                setError(null);
              }}
              spellCheck={false}
              rows={20}
              required
            />
          </label>

          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={activate}
              onChange={(e) => setActivate(e.target.checked)}
            />
            <span>Activar este schema (desactiva cualquier otro activo del mismo tenant)</span>
          </label>

          <button
            type="button"
            className={styles.buttonGhost}
            onClick={() => handleValidate()}
          >
            Validar JSON
          </button>

          {validationOk && (
            <div className={styles.success}>
              <CheckCircle2 size={14} aria-hidden /> {validationOk}
            </div>
          )}
          {error && (
            <div className={styles.error}>
              <AlertCircle size={14} aria-hidden />{' '}
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error}</pre>
            </div>
          )}
        </div>
        <div className={styles.modal__footer}>
          <button type="button" className={styles.buttonGhost} onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className={styles.button} disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear schema'}
          </button>
        </div>
      </form>
    </div>
  );
}

function exampleSchema(): string {
  return JSON.stringify(
    {
      version: 1,
      description: 'Schema demo — reemplaza las secciones por las del sitio del cliente.',
      sections: {
        home: {
          type: 'object',
          label: 'Home',
          fields: {
            hero: {
              type: 'object',
              label: 'Hero',
              fields: {
                titulo: { type: 'string', label: 'Título', required: true, max: 80 },
                subtitulo: { type: 'text', label: 'Subtítulo', max: 200 },
              },
            },
          },
        },
      },
    },
    null,
    2,
  );
}
